import { Box, Grid, Tab, Tabs, Typography } from "@mui/material";
import { useAppSelector } from "app/hooks";
import DateRangeWithModal from "components/FormControls/DateRangeWithModal/DataRangeWithModal";
import { OLMenuButton } from "components/FormControls/OLMenuButton";
import { Item } from "components/FormControls/Select/Categories";
import { SelectInput } from "components/FormControls/Select/Select";
import { ReactComponent as ChevronDownIcon } from "components/Icons/chevron-down.svg";
import { ReactComponent as HelpCircleIcon } from "components/Icons/help-circle.svg";
import { ReactComponent as Link03Icon } from "components/Icons/link-03.svg";
import { ReactComponent as Settings01Icon } from "components/Icons/settings-01.svg";
import { Tooltip } from "components/Tooltips/Tooltip";
import { ExternalLink } from "ExternalLink";
import { Form, Formik } from "formik";
import { useContextMenu } from "hooks/contextMenu";
import { useMessage } from "hooks/intl";
import { LinkItem } from "LinkItem";
import { nanoid } from "nanoid";
import { OLPaper } from "OLPaper";
import React, { Dispatch, SetStateAction, useState } from "react";
import { selectCurrentStoreData } from "reduxState/currentStoreData/currentStoreDataSlice";
import {
  DefaultWalletDataState,
  selectDefaultWalletData,
} from "reduxState/defaultWalletData/defaultWalletDataSlice";
import {
  AggregationTypeUnion,
  ChartInterval,
  ChartTab,
  LineChartData,
} from "types/charts";
import { StoreData } from "types/stores";
import { WalletTypeListItem } from "types/wallets";
import {
  abbreviateNumber,
  formatCurrencyValue,
  formatNumberValue,
} from "utilities/numbers";
import { LineChart } from "../LineChart";
import { DownloadChart } from "./DownloadChart/DownloadChart";
import { useStyles } from "./LineChartOverview.styles";
import {
  chartIntervals,
  formatDate,
  getFormattedSelectedDate,
  globalChartIntervals,
} from "./utils";

interface LineChartOverviewProps<T> {
  data: LineChartData[];
  tabs: ChartTab[];
  activeTab: string;
  chartName?: string;
  activeIntervalSettings?: ChartInterval;
  aggregationType: AggregationTypeUnion;
  setActiveIntervalSettings?: Dispatch<SetStateAction<ChartInterval>>;
  setActiveTab: Dispatch<SetStateAction<T>>;
  handleChangeSelectedWallet?: Dispatch<
    SetStateAction<WalletTypeListItem | DefaultWalletDataState | undefined>
  >;
  enableDownload?: boolean;
  intervalLabel?: string;
  walletsDropdown?: {
    anyToFetch: boolean;
    data: WalletTypeListItem[];
    selected?: WalletTypeListItem | DefaultWalletDataState | undefined;
    loadMore: () => void;
    setTypeSearch: Dispatch<SetStateAction<string>>;
  };
  storesDropdown?: {
    anyToFetch: boolean;
    data: (StoreData & { disabled: boolean })[];
    selected?: Item[];
    loadMore: () => void;
    setTypeSearch: Dispatch<React.SetStateAction<string | undefined>>;
    setSelectedStore: (store: StoreData) => void;
  };
  showLegend?: boolean;
  isGlobalAnalytics?: boolean;
}

export function LineChartOverview<T>({
  data,
  tabs,
  activeTab,
  chartName,
  aggregationType,
  activeIntervalSettings,
  setActiveIntervalSettings,
  setActiveTab,
  handleChangeSelectedWallet,
  enableDownload,
  intervalLabel,
  walletsDropdown,
  storesDropdown,
  showLegend = true,
  isGlobalAnalytics,
}: LineChartOverviewProps<T>) {
  const classes = useStyles();
  const { message } = useMessage();
  const { handleMenuClose: handleDateRangeMenuClose } = useContextMenu();
  const { currency } = useAppSelector(selectCurrentStoreData);
  const defaultWallet = useAppSelector(selectDefaultWalletData);
  const [isDateRangeModalOpen, setIsDateRangeModalOpen] = useState(false);
  const unitsName =
    (walletsDropdown?.selected && walletsDropdown.selected.unitPluralName) ||
    defaultWallet?.unitPluralName;
  const defaultChartName = chartName || "chart";

  const isCustomDateRangeSelected =
    activeIntervalSettings?.intervalLabel === "customDateRange";
  const chartDataTestId = `${defaultChartName}-line-chart`;

  const formattedIntervalStartDate = getFormattedSelectedDate(
    activeIntervalSettings?.intervalStartDate,
  );
  const formattedIntervalEndDate = getFormattedSelectedDate(
    activeIntervalSettings?.intervalEndDate,
  );

  const formatTabValue = ({
    formatCurrency,
    formatNumber,
    formatToPercentage,
    value,
  }: ChartTab) => {
    if (formatCurrency) {
      return formatCurrencyValue(value);
    } else if (formatNumber) {
      return formatNumberValue(value);
    } else if (formatToPercentage) {
      return `${value}%`;
    } else {
      return value;
    }
  };

  const getTabValue = (tab: ChartTab) => {
    const oneMillion = 1000000;

    if (tab.value >= oneMillion) {
      return abbreviateNumber(tab.value);
    } else {
      return formatTabValue(tab);
    }
  };

  const handleSubmitCustomDateRange = (startDate: Date, endDate: Date) => {
    setActiveIntervalSettings?.({
      intervalLabel: "customDateRange",
      intervalStartDate: formatDate(startDate),
      intervalEndDate: formatDate(endDate),
    });
    setIsDateRangeModalOpen(false);
    handleDateRangeMenuClose();
  };

  const handleDateRangeMenuItemClick = (item: ChartInterval) => {
    if (item.intervalLabel === "customDateRange") {
      return setIsDateRangeModalOpen(true);
    }

    setActiveIntervalSettings?.({
      intervalLabel: item.intervalLabel,
      aggregationType: item.aggregationType,
      intervalStartDate: item.intervalStartDate,
      intervalEndDate: item.intervalEndDate,
    });
    handleDateRangeMenuClose();
  };

  const walletsItems = walletsDropdown?.data
    ? walletsDropdown.data.map((wallet) => ({
        id: wallet?.code,
        label: wallet?.name,
      }))
    : [];

  const storesItems = storesDropdown?.data
    ? storesDropdown.data.map(({ storeId, name, disabled }) => ({
        id: storeId,
        label: name,
        disabled,
      }))
    : [];

  const dataTransformedForDownload =
    data &&
    data.map((d) =>
      d?.data?.map(({ x, y }) => ({
        date: x,
        value: y,
        dataSetName: d.legendLabel,
      })),
    );

  return (
    <>
      {chartName && (
        <span className={classes.header}>
          {message({ id: chartName }, { units: unitsName })}
        </span>
      )}
      <OLPaper
        className={classes.paperContainer}
        data-test-id={chartDataTestId}
      >
        <DateRangeWithModal
          isOpen={isDateRangeModalOpen}
          startDate={activeIntervalSettings?.intervalStartDate || null}
          endDate={activeIntervalSettings?.intervalEndDate || null}
          closeModal={() => {
            handleDateRangeMenuClose();
            setIsDateRangeModalOpen(false);
          }}
          onSubmit={(startDate: Date, endDate: Date) =>
            handleSubmitCustomDateRange(startDate, endDate)
          }
          data-test-id={chartDataTestId}
        />
        <div id={defaultChartName}>
          <Tabs
            allowScrollButtonsMobile
            aria-label={`${defaultChartName} tabs`}
            className={`${classes.indicator} ${classes.scrollButton}`}
            onChange={(_: React.SyntheticEvent, newValue: T) =>
              setActiveTab(newValue)
            }
            scrollButtons
            value={activeTab}
            variant="scrollable"
          >
            {tabs.map((tab) => (
              <Tab
                className={classes.tabContainer}
                key={nanoid()}
                data-test-id={`${chartDataTestId}-${tab.name}-tab`}
                label={
                  <Box className={classes.tabContent}>
                    <Grid alignItems="center" container direction="row" gap={2}>
                      <Typography
                        className={classes.tabName}
                        data-test-id={`${chartDataTestId}-${tab.name}-tab-title`}
                      >
                        {message(
                          { id: tab.name },
                          {
                            currency,
                            units: unitsName || "",
                          },
                        )}
                      </Typography>
                      <Tooltip
                        data-test-id={`${chartDataTestId}-${tab.name}-tab-tooltip`}
                        text={
                          <>
                            <Typography className={classes.tooltipTitle}>
                              {message(
                                { id: tab.name },
                                {
                                  currency,
                                  units: unitsName || "",
                                },
                              )}
                            </Typography>
                            <div className={classes.tooltipText}>
                              {tab.tooltip.text}
                            </div>
                            <Box className={classes.tooltipLinks}>
                              {tab.tooltip.internalLink?.to && (
                                <LinkItem
                                  blueSmall
                                  className={classes.link}
                                  to={tab.tooltip.internalLink.to}
                                  data-test-id={`${chartDataTestId}-${tab.name}-tab-tooltip-internal-link`}
                                >
                                  <span>{tab.tooltip.internalLink.text}</span>
                                  <Settings01Icon className={classes.icon} />
                                </LinkItem>
                              )}
                              {tab.tooltip.externalLink && (
                                <ExternalLink
                                  endIcon={
                                    <Link03Icon className={classes.icon} />
                                  }
                                  path={tab.tooltip.externalLink.path}
                                  data-test-id={`${chartDataTestId}-${tab.name}-tab-tooltip-external-link`}
                                  className={classes.link}
                                >
                                  <span>
                                    {message({ id: "documentation" })}
                                  </span>
                                </ExternalLink>
                              )}
                            </Box>
                          </>
                        }
                      >
                        <HelpCircleIcon className={classes.tooltipIcon} />
                      </Tooltip>
                    </Grid>
                    <Tooltip
                      text={
                        <Typography className={classes.tooltipTitle}>
                          {formatTabValue(tab)}
                        </Typography>
                      }
                    >
                      <span
                        className={classes.tabValue}
                        data-test-id={`${chartDataTestId}-${tab.name}-tab-value`}
                      >
                        {tab.value ? getTabValue(tab) : "-"}
                      </span>
                    </Tooltip>
                  </Box>
                }
                value={tab.query || tab.name}
              />
            ))}
          </Tabs>
          <LineChart
            data={data}
            aggregationType={aggregationType}
            tooltip={message(
              { id: activeTab },
              { currency, units: unitsName || "" },
            )}
            showLegend={showLegend}
          />
        </div>
        <div className={classes.bottomSection}>
          <div className={classes.elemWrapper}>
            {!!intervalLabel ? (
              <Typography fontSize={13}>{intervalLabel}</Typography>
            ) : (
              <div className={classes.menu}>
                <OLMenuButton
                  className={classes.dateDropdownSelector}
                  endIcon={
                    <ChevronDownIcon className={classes.dateRangeMenuIcon} />
                  }
                  submit={
                    !isCustomDateRangeSelected
                      ? message({ id: activeIntervalSettings?.intervalLabel })
                      : message(
                          {
                            id: "rangePickerFromTo",
                          },
                          {
                            from: formattedIntervalStartDate,
                            to: formattedIntervalEndDate,
                            span: (chunks: React.ReactNode[]) => (
                              <span className={classes.highlighted}>
                                {chunks}
                              </span>
                            ),
                          },
                        )
                  }
                  items={(isGlobalAnalytics
                    ? globalChartIntervals
                    : chartIntervals
                  )?.map((item) => ({
                    onClick: () => handleDateRangeMenuItemClick(item),
                    id: item.intervalLabel,
                    label: message({ id: item.intervalLabel }),
                  }))}
                  data-test-id={`${chartDataTestId}-interval-menu-button`}
                  data-sentry-unmask
                />
              </div>
            )}
            {walletsDropdown?.selected && (
              <div className={classes.menu}>
                <Formik
                  initialValues={{
                    wallet: walletsDropdown?.selected?.code,
                  }}
                  onSubmit={() => {}}
                >
                  {({ handleSubmit, values }) => (
                    <Form onSubmit={handleSubmit}>
                      <SelectInput
                        displaySearch
                        name="wallet"
                        fieldType="button"
                        items={walletsItems}
                        asyncSearch={(searchedQuery: string) =>
                          walletsDropdown?.setTypeSearch(searchedQuery)
                        }
                        onChange={(e) =>
                          handleChangeSelectedWallet?.(
                            walletsDropdown?.data?.find(
                              ({ code }) => e.target.value === code,
                            ),
                          )
                        }
                        initialValue={{
                          id: values.wallet || "",
                          label: walletsDropdown?.selected?.name || "",
                        }}
                        data-test-id={`${chartDataTestId}-wallets`}
                        anyElementsToFetch={walletsDropdown?.anyToFetch}
                        loadMoreElements={walletsDropdown?.loadMore}
                      />
                    </Form>
                  )}
                </Formik>
              </div>
            )}
            {storesDropdown?.selected && storesDropdown.selected.length > 0 && (
              <div className={classes.menu}>
                <Formik
                  initialValues={{
                    stores: [storesDropdown.selected[0]?.id],
                  }}
                  onSubmit={() => {}}
                >
                  {({ handleSubmit, values }) => (
                    <Form onSubmit={handleSubmit}>
                      <SelectInput
                        multiple
                        displaySearch
                        name="stores"
                        fieldType="button"
                        items={storesItems}
                        asyncSearch={(searchedQuery: string) =>
                          storesDropdown.setTypeSearch(searchedQuery)
                        }
                        onChange={(e) => {
                          const selectedStore = storesDropdown.data.find(
                            ({ storeId }) => e.target.value === storeId,
                          );
                          if (selectedStore) {
                            storesDropdown.setSelectedStore?.(selectedStore);
                          }
                        }}
                        initialValue={{
                          id: values?.stores[0],
                          label:
                            (storesDropdown.selected &&
                              storesDropdown.selected[0]?.label) ||
                            "",
                        }}
                        data-test-id={`${chartDataTestId}-stores`}
                        anyElementsToFetch={storesDropdown?.anyToFetch}
                        loadMoreElements={storesDropdown?.loadMore}
                      />
                    </Form>
                  )}
                </Formik>
              </div>
            )}
          </div>
          {enableDownload && (
            <DownloadChart
              imgContainerId={defaultChartName}
              chartName={message(
                { id: activeTab },
                {
                  currency,
                  units: unitsName || "",
                },
              )}
              data={dataTransformedForDownload}
            />
          )}
        </div>
      </OLPaper>
    </>
  );
}
